import {
  AppError,
  CommonErrorsKey,
  UnknownError,
  contractBook,
} from "@eulerxyz/core-sdk"

import { getConnectorClient } from "@wagmi/core"
import type { ChainIdParameter } from "@wagmi/core/internal"
import type {
  BaseErrorType,
  DecodeErrorResultReturnType,
  Evaluate,
  Hex,
  RawContractError,
} from "viem"
import {
  BaseError,
  type ContractFunctionExecutionError,
  decodeErrorResult,
} from "viem"
import { call, getTransaction } from "viem/actions"
import type {
  GetConnectorClientErrorType,
  ReadContractsErrorType,
} from "wagmi/actions"
import type { ErrorType, ExtendedConfig } from "../types"
import { isValidAbi } from "../utils/abi"

export type GetEVCTransactionErrorParameters<
  config extends ExtendedConfig = ExtendedConfig,
> = Evaluate<
  ChainIdParameter<config> & {
    hash?: Hex
  }
>

export type GetEVCTransactionErrorReturnType = DecodeErrorResultReturnType

export type GetEVCTransactionErrorErrorType =
  // getConnectorClient()
  | GetConnectorClientErrorType
  // base
  | BaseErrorType
  | ErrorType
  // viem
  | ReadContractsErrorType
  | ContractFunctionExecutionError
  // Custom
  | UnknownError
  | AppError

function getRevertErrorData(err: unknown) {
  if (!(err instanceof BaseError)) return
  const error = err.walk() as RawContractError
  return typeof error?.data === "object" ? error.data?.data : error.data
}

const ALLABIS = Object.values(contractBook).map((e) => e.abi)

export async function getEVCTransactionError<config extends ExtendedConfig>(
  config: config,
  parameters: GetEVCTransactionErrorParameters,
): Promise<GetEVCTransactionErrorReturnType> {
  const { hash, chainId = 1 } = parameters
  if (!hash)
    throw new AppError(CommonErrorsKey.ParamRequired, "Hash is required")

  const client = await getConnectorClient(config, {
    chainId,
  })
  const txn = await getTransaction(client, { hash })

  try {
    await call(client, {
      ...txn,
      batch: false,
      data: txn.input,
      to: txn.to,
      account: txn.from,
      gasPrice: txn.type !== "eip1559" && txn.gasPrice,
      maxFeePerGas: txn.type === "eip1559" && txn.maxFeePerGas,
      maxPriorityFeePerGas: txn.type === "eip1559" && txn.maxPriorityFeePerGas,
    })
  } catch (e) {
    const data = getRevertErrorData(e)

    if (data) {
      for (const abi of ALLABIS) {
        if (!isValidAbi(abi)) continue
        try {
          return decodeErrorResult({ abi, data })
        } catch {}
      }
    }
  }

  throw new UnknownError(
    CommonErrorsKey.UnknownError,
    "Transaction did not revert",
  )
}
